/** The workspace brand kit as the editor holds it (loaded once per page, replaced after each save). */
import { api, ApiError, type BrandKit, type StoredBrandKit } from '../api.ts';

type Listener = () => void;

export class BrandCache {
  stored: StoredBrandKit | undefined;
  error: string | undefined;
  version = 0;
  #listeners = new Set<Listener>();
  #loading: Promise<void> | undefined;

  subscribe = (fn: Listener): (() => void) => {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  };
  getVersion = (): number => this.version;
  #emit(): void {
    this.version++;
    for (const l of this.#listeners) l();
  }

  get kit(): BrandKit {
    return this.stored?.kit ?? { colors: [], fonts: [], textStyles: [], logos: [] };
  }

  load(force = false): Promise<void> {
    if (this.#loading && !force) return this.#loading;
    this.#loading = api.brandKit().then(
      (s) => {
        this.stored = s;
        this.error = undefined;
        this.#emit();
      },
      (e: unknown) => {
        this.error = e instanceof Error ? e.message : String(e);
        this.#emit();
      },
    );
    return this.#loading;
  }

  /** Save a changed kit (409: someone else changed it; the latest is loaded and the error says so). */
  async save(kit: BrandKit): Promise<boolean> {
    try {
      this.stored = await api.saveBrandKit(this.stored?.revision ?? 0, kit);
      this.error = undefined;
      this.#emit();
      return true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
      if (e instanceof ApiError && e.code === 'REVISION_CONFLICT') await this.load(true);
      this.#emit();
      return false;
    }
  }
}

export const brandCache = new BrandCache();
